// SessionManager.js
import { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { API_URL } from './config/apiConfig';

function isTokenExpired(token) {
  if (!token) return true;
  try {
    const { exp } = jwtDecode(token);
    return Date.now() >= exp * 1000;
  } catch {
    return true;
  }
}

// Function to refresh the access token using the refresh token
async function refreshAccessToken() {
  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) {
    throw new Error('No refresh token available');
  }

  try {
    const response = await fetch(`${API_URL}/refresh-token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refreshToken }),
    });

    if (!response.ok) {
      throw new Error('Failed to refresh token');
    }

    const data = await response.json();
    localStorage.setItem('token', data.token);
    return data.token;
  } catch (error) {
    console.error('Token refresh failed:', error);
    throw error;
  }
}

export default function SessionManager({ onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;

    try {
      const { exp } = jwtDecode(token);
	  
      const expiresAt = new Date(exp * 1000);
      const msUntilExpiry = exp * 1000 - Date.now();

      console.log("Token expires at:", expiresAt.toLocaleString());
      console.log("Milliseconds until expiry:", msUntilExpiry);

      // If token is already expired, try to refresh it
      if (Date.now() >= exp * 1000) {
        console.log("Token already expired. Attempting to refresh...");
        refreshAccessToken()
          .then((newToken) => {
            console.log("Token refreshed successfully");
            // Token refreshed, no need to log out
          })
          .catch((error) => {
            console.log("Token refresh failed:", error);
            localStorage.clear();
            alert("Session expired. Please log in again.");
            onLogout?.();
            navigate("/");
          });
        return;
      }

      // Set up timer to refresh token before it expires (refresh 1 minute before expiry)
      const refreshTime = exp * 1000 - Date.now() - 60000; // 1 minute before expiry
      const timer = setTimeout(async () => {
        try {
          console.log("Refreshing token before expiry...");
          await refreshAccessToken();
          console.log("Token refreshed successfully");
        } catch (error) {
          console.log("Token refresh failed:", error);
          localStorage.clear();
          alert("Session expired. Please log in again.");
          onLogout?.();
          navigate("/");
        }
      }, refreshTime);

      return () => clearTimeout(timer);
    } catch {
      localStorage.clear();
      onLogout?.();
      navigate("/");
    }
  }, [location, navigate, onLogout]);

  return null; // This component doesn't render anything
}
